/* eslint-disable max-len */

import {useCallback, MouseEvent} from 'react';
import {useHistory} from 'react-router-dom';
import {isNumber} from 'lodash-es';
import classNames from 'classnames';
import {
    User,
    USERINFO_TOOLTIP_CARD_TYPE,
    UserInfoTooltipCardType,
    ICoinDocList,
    ACHIEVE_ICON_SOURCE,
} from '@/interface';
import {URL_SOURCE} from '@/Constant';
import routers, {buildPath, ServerConfig} from '@/config/routers';
import Tooltip, {TooltipTrigger} from '@/components/Tooltip';
import Text, {TextType} from '@/components/Text';
import Avatar, {AvatarSize} from '@/components/Avatar';
import Anchor, {OpenType} from '@/components/Anchor';
import {isHi, hiOpenChat} from '@/utils/browserUtils';
import Icon, {IconSize} from '@/components/Icon';
import {OpenChatSvg, OpenPersonSvg, OpenChatSvgHover, OpenPersonSvgHover} from '@/icons';
import HoverIcon from '@/components/Icon/hoverIcon';
import kuPiper from '@/middleware/kuPiper';
import DocLink from '@/components/DocLink';
import {getDocFillSvgByType} from '@/utils/docType';
import {openPersonFromUserTooltipContent, docLinkFromUserTooltipContent} from '@/buried/common';
import styles from './index.module.scss';
import AchieveIcon from '@/domains/user/PersonalHome/UserInfo/Achieve/AchieveIcon';
import {getAchieveTooltipContent} from '@/domains/user/PersonalHome/UserInfo/Achieve/utils';
import {fromByteArray} from 'base64-js';
import sdk from '@/sdk';
import {GRAY_RELEASE_KEYS, initGray} from '@/grayRelease';
import {
    feLogger,
    openRLDialog,
} from '@/buried';
import ExternalLabel from '../ExternalLabel';
import {isSidebarView, openLinkProxy} from '@/hooks/queryViewController';

const arrangeAchieveCount = (num: any) => {
    if (isNumber(num)) {
        return <span>{num}</span>;
    }
    return '--';
};

interface IUserCardContent extends Partial<User> {
    avatarClass?: string;
    cardType: UserInfoTooltipCardType;
    isShowAvatar: boolean;
    isShowCoinDocList?: boolean;
    isShowAddPermission?: boolean;
    openRlChat?: (e: any) => void;
    openEmail?: (e: any) => void;
    openPersonalHomeUrl?: (e: any) => void;
    onAddPermission?: () => void;
    docUrl?: string;
    isVirtualUser?: boolean;
    isExternal?: boolean; // 是否外部成员
    passportDisplayName?: string; // 外部账号显示的名称
}


export default (props: IUserCardContent) => {
    const {
        userGuid,
        isShowAvatar,
        avatarClass,
        cardType,
        department,
        email,
        avatar,
        nickname,
        likeCount,
        receivedCoinCount,
        rlName,
        squareUrl,
        tags,
        coinDocList,
        isShowCoinDocList = true,
        isShowAddPermission,
        medal,
        openRlChat,
        openEmail,
        openPersonalHomeUrl,
        onAddPermission,
        rlId,
        canOpenSession,
        docUrl,
        label,
        snapshot,
        isVirtualUser,
        resignStatus,
        isExternal,
        passportDisplayName = '',
    } = props;
    const isInHi = isHi();
    const history = useHistory();
    const isEditor = cardType === USERINFO_TOOLTIP_CARD_TYPE.editor;
    const isSearch = cardType === USERINFO_TOOLTIP_CARD_TYPE.search;
    const isNormal = cardType === USERINFO_TOOLTIP_CARD_TYPE.normal;
    const showAvatar = isShowAvatar && !isSearch;

    const getJumpUrl = (docList: ICoinDocList): string => {
        const {spaceGuid = '', groupGuid = '', repositoryGuid = '', docGuid = ''} = docList;
        const query = URL_SOURCE.PersonalCardDocList;
        const url = buildPath(routers.Knowledge.path, {
            spaceGuid,
            groupGuid,
            repositoryGuid,
            docGuid,
        }) + `?source=${query}`;
        return url;
    };
    const handleClickCardTop = (e: MouseEvent) => {
        e.stopPropagation();
        if (!isSearch) {
            openPersonFromUserTooltipContent();
            const url = buildPath(routers.PersonalHome.path, {userName: avatar || ''});
            const host = ServerConfig.origin;
            !isSearch && openLinkProxy(`${host}${url}`);
        }
    };
    const handleClickDocItem = useCallback((e?: MouseEvent) => {
        e && e.stopPropagation();
        docLinkFromUserTooltipContent();
    }, []);

    const handleClickAddPermission = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        onAddPermission && onAddPermission();
    }, [onAddPermission]);

    const handleOpenRlChat = useCallback((e) => {
        if (!rlName) {
            return;
        }
        e.preventDefault();
        if (isInHi) {
            rlName && hiOpenChat(rlName);
        } else {
            const path = buildPath(routers.RuLiu.path, {ruLiuId: rlName || ''});
            window.open(path, '_self');
        }
        openRlChat && openRlChat(e);
    }, [isInHi, openRlChat, rlName]);

    const handleEmail = useCallback((e) => {
        e.stopPropagation();
        openEmail && openEmail(e);
    }, [openEmail]);

    const compile = (apiString: string) => {
        const url = new URL('infoflow://APICenter');
        const byteArray = new TextEncoder().encode(apiString);
        // 转base64编码
        const apiBase64 = fromByteArray(byteArray);
        // 转url编码
        const apiEncode = encodeURIComponent(apiBase64);
        // 设置data参数 拼接url
        const queryString = `data=${apiEncode}`;
        const finalUrl = `${url.href}?${queryString}`;
        return finalUrl;
    };

    const handleOpenChat = (e: any) => {
        e.stopPropagation();

        // 打开页面埋点上报 yes传入了docUrl
        feLogger(openRLDialog({
            withdoclink: docUrl ? 'yes' : 'no',
        }));

        const data: any = {
            APIName: 'BdHiJs.service.hi.open',
            version: '65',
            data: {
                id: rlId,
                type: '1',
                source: '10002',
                appname: '知识库',
            },
        };
        // 判断是否需要传递context，如果需要传递，则将docUrl作为context的content
        if (docUrl) {
            data.data.context = [
                {
                    type: 'url',
                    content: docUrl,
                },
            ];
        }
        const openUrl = compile(JSON.stringify(data));
        // window.open(openUrl);
        openLinkProxy(openUrl);
    };

    const isNoPermissionInEditor = isEditor && isShowAddPermission;

    const showDocList = !isSearch && isShowCoinDocList && !!coinDocList?.length;

    // 添加resignStatus字段 1离职， 0在职，用于判断是否显示对话按钮
    const showOpenDialogue = !(rlId === sdk.user!.rlId) && !!rlId && canOpenSession && resignStatus !== 1;


    if (isExternal) {
        return <div className={styles.externalUserCardContent}>
            <Avatar
                className={classNames(styles.externalAvatar, avatarClass)}
                userGuid={userGuid}
                size={AvatarSize.SM}
            />
            <div>
                <div className={styles.externalUserCardContentName}>
                    <div>
                        {nickname && nickname.length && `${nickname}`}
                    </div>
                    <ExternalLabel labelName="外部" tipsDisabled />
                </div>
                <div className={styles.externalUserCardContentInfo}>
                    <span>
                        {department ? department : '- -'}
                    </span>
                    <span className={styles.exterLine}>
                        |
                    </span>
                    <span className={styles.externalPassportName}>
                        {passportDisplayName}
                    </span>
                </div>
            </div>
        </div>;
    }

    return (<>
        <div className={classNames(styles.userCardContentWrapper, isVirtualUser && styles.userCardContentWrapperVirtual)}>
            <div className={styles.userCardContentWrapperTop}>
                <div className={styles.userCardContentWrapperContent} onClick={handleClickCardTop}>
                    {
                        // 卡片内容保持一致，都显示头像
                        <Avatar
                            className={classNames(styles.tooltipAvatar, avatarClass)}
                            userGuid={userGuid}
                            size={AvatarSize.SM}
                        />
                    }
                    <div className={styles.tooltipWrapperInfo}>
                        <div className={styles.userNameBox}>
                            <div className={styles.userName}>
                                <div className={
                                    classNames(styles.tooltipTextName,
                                        {
                                            [styles.tooltipTextNameVirtual]: isVirtualUser && label, // 如果是虚拟用户且有label
                                            [styles.tooltipTextNameLabel]: isVirtualUser && (!label || label === ''),
                                        }
                                    )}
                                >
                                    {nickname && nickname.length && `${nickname}`}
                                </div>
                                <div className={styles.achieveIconBox}>
                                    {
                                        !isVirtualUser && !isSearch && medal && !!medal.length && medal.map(item => {
                                            return (
                                                <div key={item.type} className={styles.achieveIconItem}>
                                                    <Tooltip
                                                        trigger={TooltipTrigger.Hover}
                                                        content={getAchieveTooltipContent(item, item.type)}
                                                        placement={'top'}
                                                        mouseEnterDelay={300}
                                                    >
                                                        <div className={styles.achieveIcon}>
                                                            <AchieveIcon
                                                                level={item.level}
                                                                type={item.type}
                                                                source={ACHIEVE_ICON_SOURCE.exposedTopIcon}
                                                                isExposed
                                                            />
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                            );
                                        })
                                    }
                                </div>
                                {
                                    label && (
                                        <div className={styles.userLabel}>
                                            <Tooltip
                                                trigger={TooltipTrigger.Hover}
                                                // content={snapshot}
                                                mouseEnterDelay={300}
                                            >
                                                <div className={styles.aiTag}>
                                                    {label}
                                                </div>
                                            </Tooltip>
                                        </div>
                                    )
                                }
                            </div>
                            <div className={styles.userCardBtn}>
                                {
                                    !!isSearch && kuPiper.openNormalReplaceSlot(
                                        {
                                            scope: kuPiper.slotScopeMap.UserInfoTip,
                                            position: kuPiper.slotPositionMap.UserInfoTip.RuliuIcon,
                                            DefaultComponent: rlName && <Tooltip
                                                type={TooltipTrigger.Hover}
                                                content={'打开如流会话'}
                                                placement={'top'}
                                            >
                                                <div className={styles.tooltipTextIconBox}>
                                                    <a
                                                        href={rlName}
                                                        onClick={handleOpenRlChat}
                                                        className={styles.tooltipTextIcon}
                                                    >
                                                        <HoverIcon svg={OpenChatSvg} hoverSvg={OpenChatSvgHover} />
                                                    </a>
                                                </div>
                                            </Tooltip>,
                                        }
                                    )
                                }
                                {
                                    !!isSearch && kuPiper.openNormalReplaceSlot(
                                        {
                                            scope: kuPiper.slotScopeMap.UserInfoTip,
                                            position: kuPiper.slotPositionMap.UserInfoTip.PersonalPageIcon,
                                            DefaultComponent: !isInHi && squareUrl && <Tooltip
                                                type={TooltipTrigger.Hover}
                                                content={'打开个人主页'}
                                                placement={'top'}
                                            >
                                                <div className={styles.tooltipTextIconBox}>
                                                    <a
                                                        className={styles.tooltipTextIcon}
                                                        href={squareUrl}
                                                        onClick={openPersonalHomeUrl}
                                                    >
                                                        <HoverIcon svg={OpenPersonSvg} hoverSvg={OpenPersonSvgHover} />
                                                    </a>
                                                </div>
                                            </Tooltip>,
                                        }
                                    )
                                }
                                {/* 新版图标 */}
                                {
                                    !isSearch && showOpenDialogue && <div
                                        className={classNames(styles.personalBtn, styles.openDialogueBtn)}
                                        onClick={(e) => {handleOpenChat(e);}}
                                    >打开会话</div>
                                }
                                {
                                    !isVirtualUser && !isSearch && <div className={styles.personalBtn}>TA的主页</div>
                                }
                            </div>
                        </div>
                        {
                            !isSearch &&
                            <div className={styles.notSearchTooltipText}>
                                <Text type={TextType.T14} className={styles.tooltipTextDep}>
                                    {department ? department : '- -'}
                                    {/* {department} */}
                                </Text>
                                <div className={styles.sep}></div>
                                <Text type={TextType.T12} className={styles.tooltipTextEmail}>
                                    {email}
                                </Text>
                            </div>
                        }
                        {
                            !isVirtualUser && !isSearch ?
                                <p className={styles.myAchievements}>
                                    文档累计获赠{arrangeAchieveCount(receivedCoinCount)}枚知识币，获赞{arrangeAchieveCount(likeCount)}次
                                </p> : <p className={styles.myAchievements}>{snapshot}</p>
                        }
                    </div>
                </div>
            </div>
            {
                !isVirtualUser && showDocList &&
                <div className={classNames(
                    // 布局不合理 暂时这样处理 等旧版本卡片下线后调整
                    styles.userCardContentWrapperBottom,
                    showAvatar && styles.showAvatar
                )}
                >
                    {
                        coinDocList.map((ite: ICoinDocList) => {
                            return (
                                <Anchor
                                    className={styles.AnchorColor}
                                    url={getJumpUrl(ite)}
                                    openType={OpenType.newTab}
                                    onClick={handleClickDocItem}
                                    key={ite.docGuid}
                                >
                                    <div
                                        className={
                                            classNames(
                                                styles.docItem
                                            )}
                                    >
                                        <DocLink
                                            className={styles.docLink}
                                            svg={getDocFillSvgByType()}
                                            size={IconSize.LG}
                                            url={getJumpUrl(ite)}
                                            docName={ite?.title}
                                            openWay={OpenType.newTab}
                                        />
                                        <div className={styles.coinNum}>
                                            {ite.coinCount}币
                                        </div>
                                    </div>
                                </Anchor>
                            );
                        })
                    }
                </div>
            }
            {
                !isVirtualUser && isNoPermissionInEditor && <div className={styles.addPermission} onClick={handleClickAddPermission}>
                    <span>授予查看权限</span>
                </div>
            }
            {/* 搜索卡片 */}
            {
                !!isSearch && <div className={styles.userCardContentWrapperBottom}>
                    {
                        email && (<div className={styles.searchTooltipText}>
                            <span className={styles.tooltipLabel}>邮箱</span>
                            <a
                                className={styles.tooltipTextEmail}
                                href={`mailto: ${email}`}
                                onClick={handleEmail}
                                title={email}
                            >
                                {email}
                            </a>
                        </div>)
                    }
                    {
                        tags && (<div className={styles.tooltipTagText}>
                            <span className={styles.tooltipLabel}>标签</span>
                            <Text
                                type={TextType.T13}
                                className={styles.tooltipTextTag}
                                title={tags}
                            >
                                {tags}
                            </Text>
                        </div>)
                    }
                </div>
            }
        </div>
    </>);
};
